import type { LeadContactInfo, PreferredContactMethod } from "./types.js";

/** Raw, untrusted values straight off the lead capture form - nothing here is assumed normalized or safe yet. */
export interface LeadFormRawInput {
  name: string;
  email?: string;
  phone?: string;
  preferredContactMethod: string;
  postalCode: string;
  desiredStartDate?: string;
  projectDescription?: string;
}

export interface FieldValidationError {
  field: string;
  code: string;
  message: string;
}

export interface LeadFormValidationResult {
  valid: boolean;
  errors: FieldValidationError[];
  /** Present only when valid - the normalized contact info ready to build a LeadRecord from. */
  value?: LeadContactInfo;
}

const CANADIAN_POSTAL_CODE_PATTERN = /^[A-CEGHJ-NPRSTVXY]\d[A-CEGHJ-NPRSTV-Z] ?\d[A-CEGHJ-NPRSTV-Z]\d$/i;

// Deliberately simple - a full RFC 5322 email validator is out of scope and
// would reject plenty of real addresses; this catches the obvious cases
// (missing @, missing domain, embedded whitespace) without being clever.
const SIMPLE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 2000;
const PREFERRED_CONTACT_METHODS: PreferredContactMethod[] = ["email", "phone", "either"];

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d]/g, "");
}

function normalizePostalCode(postalCode: string): string {
  const compact = postalCode.replace(/\s+/g, "").toUpperCase();
  return `${compact.slice(0, 3)} ${compact.slice(3)}`;
}

function isValidDateString(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime());
}

/**
 * Validates and normalizes a raw lead-form submission. Returns the field
 * errors (if any) plus, only when fully valid, a normalized LeadContactInfo
 * ready to hand to buildLeadRecord. This is purely format/consistency
 * checking - spam heuristics live in spam-protection.ts, since "well-formed"
 * and "not spam" are independent questions.
 */
export function validateLeadForm(raw: LeadFormRawInput): LeadFormValidationResult {
  const errors: FieldValidationError[] = [];

  const name = raw.name?.trim() ?? "";
  if (name.length < 2) {
    errors.push({ field: "name", code: "name_too_short", message: "Please enter your name." });
  } else if (name.length > MAX_NAME_LENGTH) {
    errors.push({ field: "name", code: "name_too_long", message: `Name must be ${MAX_NAME_LENGTH} characters or fewer.` });
  }

  const preferredContactMethod = raw.preferredContactMethod as PreferredContactMethod;
  if (!PREFERRED_CONTACT_METHODS.includes(preferredContactMethod)) {
    errors.push({
      field: "preferredContactMethod",
      code: "preferred_contact_method_invalid",
      message: "Please choose how you'd like to be contacted."
    });
  }

  const email = raw.email?.trim();
  if (email && !SIMPLE_EMAIL_PATTERN.test(email)) {
    errors.push({ field: "email", code: "email_invalid", message: "That doesn't look like a valid email address." });
  }

  const phoneDigits = raw.phone ? normalizePhone(raw.phone) : undefined;
  if (raw.phone && (!phoneDigits || phoneDigits.length < 10 || phoneDigits.length > 11)) {
    errors.push({ field: "phone", code: "phone_invalid", message: "Please enter a valid 10-digit phone number." });
  }

  // Only enforce the email/phone-matches-preference rule once the method
  // itself is valid - otherwise we'd report a confusing second error for
  // the same root problem.
  if (PREFERRED_CONTACT_METHODS.includes(preferredContactMethod)) {
    const hasEmail = Boolean(email);
    const hasValidPhone = Boolean(phoneDigits && phoneDigits.length >= 10 && phoneDigits.length <= 11);
    if (preferredContactMethod === "email" && !hasEmail) {
      errors.push({ field: "email", code: "email_required", message: "Enter an email address, or choose a different contact method." });
    } else if (preferredContactMethod === "phone" && !hasValidPhone) {
      errors.push({ field: "phone", code: "phone_required", message: "Enter a phone number, or choose a different contact method." });
    } else if (preferredContactMethod === "either" && !hasEmail && !hasValidPhone) {
      errors.push({ field: "email", code: "contact_detail_required", message: "Enter an email address or phone number so we can reach you." });
    }
  }

  const postalCode = raw.postalCode?.trim() ?? "";
  if (!CANADIAN_POSTAL_CODE_PATTERN.test(postalCode)) {
    errors.push({ field: "postalCode", code: "postal_code_invalid", message: "Please enter a valid Canadian postal code (e.g. A1A 1A1)." });
  }

  if (raw.desiredStartDate && !isValidDateString(raw.desiredStartDate)) {
    errors.push({ field: "desiredStartDate", code: "desired_start_date_invalid", message: "Please enter a valid date." });
  }

  const description = raw.projectDescription?.trim();
  if (description && description.length > MAX_DESCRIPTION_LENGTH) {
    errors.push({
      field: "projectDescription",
      code: "project_description_too_long",
      message: `Project description must be ${MAX_DESCRIPTION_LENGTH} characters or fewer.`
    });
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return {
    valid: true,
    errors: [],
    value: {
      name,
      email: email || undefined,
      phone: phoneDigits,
      preferredContactMethod,
      postalCode: normalizePostalCode(postalCode),
      desiredStartDate: raw.desiredStartDate || undefined,
      projectDescription: description || undefined,
      consentToContact: true
    }
  };
}
